import React, { useState, useEffect } from 'react';
import { api } from '../lib/api';
import { authService } from '../lib/auth';
import { toast } from 'sonner';
import { 
  Plus, Search, User, Mail, Phone, BookOpen, Edit2, Trash2, 
  Loader2, X, Check, XCircle, Eye, EyeOff, GraduationCap
} from 'lucide-react';

const Teachers = () => {
  const [teachers, setTeachers] = useState([]);
  const [subjects, setSubjects] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [selectedTeacher, setSelectedTeacher] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    password: '',
    phone: '',
    subjects: []
  });
  
  const user = authService.getCurrentUser();

  useEffect(() => {
    fetchTeachers();
    fetchSubjects();
  }, []);

  const fetchTeachers = async () => {
    try {
      const response = await api.getAllTeachers();
      setTeachers(response.data);
    } catch (error) {
      // Fall back to regular teachers list if getAllTeachers fails
      try {
        const response = await api.getTeachers();
        setTeachers(response.data);
      } catch (err) {
        toast.error('Failed to fetch teachers');
      }
    } finally {
      setLoading(false);
    }
  };

  const fetchSubjects = async () => {
    try {
      const response = await api.getSubjects();
      setSubjects(response.data);
    } catch (error) {
      console.error('Failed to fetch subjects');
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!selectedTeacher && !formData.password) {
      toast.error('Password is required for new teachers');
      return;
    }
    
    try {
      if (selectedTeacher) {
        // Update existing teacher
        const updateData = {
          name: formData.name,
          phone: formData.phone,
          subjects: formData.subjects
        };
        if (formData.password) {
          updateData.password = formData.password;
        }
        await api.updateTeacher(selectedTeacher.id, updateData);
        toast.success('Teacher updated successfully');
      } else {
        // Create new teacher
        await api.createTeacher({
          name: formData.name,
          email: formData.email,
          password: formData.password,
          phone: formData.phone,
          subjects: formData.subjects
        });
        toast.success('Teacher created successfully');
      }
      setShowModal(false);
      resetForm();
      fetchTeachers();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to save teacher');
    }
  };

  const handleDelete = async (teacherId, teacherName) => {
    if (!window.confirm(`Are you sure you want to delete teacher "${teacherName}"? This action cannot be undone.`)) {
      return;
    }
    
    try {
      await api.deleteTeacher(teacherId);
      toast.success('Teacher deleted successfully');
      fetchTeachers();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to delete teacher');
    }
  };

  const handleToggleApproval = async (teacher) => {
    try {
      await api.updateTeacher(teacher.id, { is_approved: !teacher.is_approved });
      toast.success(`Teacher ${teacher.is_approved ? 'deactivated' : 'activated'} successfully`);
      fetchTeachers();
    } catch (error) {
      toast.error('Failed to update teacher status');
    }
  };

  const resetForm = () => {
    setFormData({
      name: '',
      email: '',
      password: '',
      phone: '',
      subjects: []
    });
    setSelectedTeacher(null);
    setShowPassword(false);
  };

  const openEditModal = (teacher) => {
    setSelectedTeacher(teacher);
    setFormData({
      name: teacher.name,
      email: teacher.email,
      password: '',
      phone: teacher.phone || '',
      subjects: teacher.subjects || []
    });
    setShowModal(true);
  };

  const handleSubjectToggle = (subjectName) => {
    setFormData(prev => {
      const newSubjects = prev.subjects.includes(subjectName)
        ? prev.subjects.filter(s => s !== subjectName)
        : [...prev.subjects, subjectName];
      return { ...prev, subjects: newSubjects };
    });
  };

  const filteredTeachers = teachers.filter(teacher => 
    teacher.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
    teacher.email.toLowerCase().includes(searchTerm.toLowerCase()) ||
    (teacher.phone && teacher.phone.includes(searchTerm))
  );

  const canManageTeachers = ['admin', 'manager'].includes(user?.role);

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="animate-spin text-indigo-600" size={32} />
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="teachers-page">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-slate-900 flex items-center gap-2">
            <GraduationCap size={28} className="text-indigo-600" />
            Teachers
          </h1>
          <p className="text-slate-600 mt-1">Manage teachers for class delivery</p>
        </div>
        {canManageTeachers && (
          <button
            onClick={() => { resetForm(); setShowModal(true); }}
            className="flex items-center gap-2 px-4 py-2 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 transition-all"
            data-testid="add-teacher-btn"
          >
            <Plus size={20} />
            <span>Add Teacher</span>
          </button>
        )}
      </div>

      {/* Search */}
      <div className="relative max-w-md">
        <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-slate-400" size={20} />
        <input
          type="text"
          placeholder="Search teachers by name, email, or phone..."
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          className="w-full pl-10 pr-4 py-2 border border-slate-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-500"
          data-testid="search-input"
        />
      </div>

      {/* Teachers Grid */}
      {filteredTeachers.length === 0 ? (
        <div className="bg-white rounded-xl border border-slate-200 p-12 text-center">
          <GraduationCap size={64} className="mx-auto text-slate-200 mb-4" />
          <h3 className="text-xl font-semibold text-slate-700 mb-2">No Teachers Found</h3>
          <p className="text-slate-500 mb-4">
            {searchTerm ? 'No teachers match your search.' : 'Add your first teacher to get started.'}
          </p>
          {canManageTeachers && !searchTerm && (
            <button
              onClick={() => { resetForm(); setShowModal(true); }}
              className="inline-flex items-center gap-2 px-4 py-2 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700"
            >
              <Plus size={20} />
              Add Teacher
            </button>
          )}
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {filteredTeachers.map((teacher) => (
            <div
              key={teacher.id}
              className={`bg-white rounded-xl border ${teacher.is_approved ? 'border-slate-200' : 'border-red-200 bg-red-50'} p-5 hover:shadow-md transition-all`}
              data-testid={`teacher-card-${teacher.id}`}
            >
              <div className="flex items-start justify-between mb-3">
                <div className="flex items-center gap-3">
                  <div className={`w-12 h-12 rounded-full flex items-center justify-center ${teacher.is_approved ? 'bg-indigo-100' : 'bg-red-100'}`}>
                    <User size={24} className={teacher.is_approved ? 'text-indigo-600' : 'text-red-600'} />
                  </div>
                  <div>
                    <h3 className="font-semibold text-slate-900">{teacher.name}</h3>
                    {!teacher.is_approved && (
                      <span className="text-xs text-red-600 font-medium">Inactive</span>
                    )}
                  </div>
                </div>
                {canManageTeachers && (
                  <div className="flex items-center gap-1">
                    <button
                      onClick={() => handleToggleApproval(teacher)}
                      className={`p-1.5 rounded hover:bg-slate-100 ${teacher.is_approved ? 'text-red-500' : 'text-emerald-500'}`}
                      title={teacher.is_approved ? 'Deactivate' : 'Activate'}
                    >
                      {teacher.is_approved ? <XCircle size={18} /> : <Check size={18} />}
                    </button>
                    <button
                      onClick={() => openEditModal(teacher)}
                      className="p-1.5 text-slate-500 hover:text-amber-600 hover:bg-amber-50 rounded"
                      title="Edit"
                    >
                      <Edit2 size={18} />
                    </button>
                    <button
                      onClick={() => handleDelete(teacher.id, teacher.name)}
                      className="p-1.5 text-slate-500 hover:text-red-600 hover:bg-red-50 rounded"
                      title="Delete"
                    >
                      <Trash2 size={18} />
                    </button>
                  </div>
                )}
              </div>
              
              <div className="space-y-2 text-sm">
                <div className="flex items-center gap-2 text-slate-600">
                  <Mail size={14} />
                  <span className="truncate">{teacher.email}</span>
                </div>
                {teacher.phone && (
                  <div className="flex items-center gap-2 text-slate-600">
                    <Phone size={14} />
                    <span>{teacher.phone}</span>
                  </div>
                )}
                {teacher.subjects && teacher.subjects.length > 0 && (
                  <div className="flex items-start gap-2 text-slate-600">
                    <BookOpen size={14} className="mt-0.5 flex-shrink-0" />
                    <div className="flex flex-wrap gap-1">
                      {teacher.subjects.map((subject, idx) => (
                        <span key={idx} className="px-2 py-0.5 bg-indigo-50 text-indigo-700 rounded text-xs">
                          {subject}
                        </span>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Create/Edit Modal */}
      {showModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl w-full max-w-lg max-h-[90vh] overflow-y-auto">
            <div className="p-6 border-b border-slate-200 flex items-center justify-between">
              <h2 className="text-xl font-semibold text-slate-900">
                {selectedTeacher ? 'Edit Teacher' : 'Add New Teacher'}
              </h2>
              <button onClick={() => setShowModal(false)} className="text-slate-400 hover:text-slate-600">
                <X size={24} />
              </button>
            </div>
            <form onSubmit={handleSubmit} className="p-6 space-y-4">
              {/* Name */}
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1">Full Name *</label>
                <input
                  type="text"
                  value={formData.name}
                  onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                  className="w-full px-4 py-2 border border-slate-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-500"
                  required
                  data-testid="teacher-name-input"
                />
              </div>

              {/* Email */}
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1">Email *</label>
                <input
                  type="email"
                  value={formData.email}
                  onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                  className="w-full px-4 py-2 border border-slate-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-500"
                  required
                  disabled={!!selectedTeacher}
                  data-testid="teacher-email-input"
                />
                {selectedTeacher && (
                  <p className="text-xs text-slate-500 mt-1">Email cannot be changed after creation</p>
                )}
              </div>

              {/* Password */}
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1">
                  Password {selectedTeacher ? '(leave blank to keep current)' : '*'}
                </label>
                <div className="relative">
                  <input
                    type={showPassword ? 'text' : 'password'}
                    value={formData.password}
                    onChange={(e) => setFormData({ ...formData, password: e.target.value })}
                    className="w-full px-4 py-2 border border-slate-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-500 pr-10"
                    required={!selectedTeacher}
                    minLength={6}
                    data-testid="teacher-password-input"
                  />
                  <button
                    type="button"
                    onClick={() => setShowPassword(!showPassword)}
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600"
                  >
                    {showPassword ? <EyeOff size={18} /> : <Eye size={18} />}
                  </button>
                </div>
              </div>

              {/* Phone */}
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1">Phone Number</label>
                <input
                  type="tel"
                  value={formData.phone}
                  onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                  className="w-full px-4 py-2 border border-slate-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-500"
                  data-testid="teacher-phone-input"
                />
              </div>

              {/* Subjects */}
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1">
                  Subjects <span className="text-xs text-slate-500">(Select subjects this teacher can teach)</span>
                </label>
                <div className="border border-slate-200 rounded-lg p-3 max-h-40 overflow-y-auto space-y-2">
                  {subjects.length === 0 ? (
                    <p className="text-slate-500 text-sm">No subjects available. Add subjects in Classes first.</p>
                  ) : (
                    subjects.map((subject) => (
                      <label key={subject.id} className="flex items-center gap-2 cursor-pointer hover:bg-slate-50 p-1 rounded">
                        <input
                          type="checkbox"
                          checked={formData.subjects.includes(subject.name)}
                          onChange={() => handleSubjectToggle(subject.name)}
                          className="w-4 h-4 text-indigo-600 rounded focus:ring-indigo-500"
                        />
                        <span className="text-sm text-slate-700">{subject.name}</span>
                      </label>
                    ))
                  )}
                </div>
                {formData.subjects.length > 0 && (
                  <p className="text-xs text-indigo-600 mt-1">
                    Selected: {formData.subjects.join(', ')}
                  </p>
                )}
              </div>

              <div className="flex justify-end gap-3 pt-4">
                <button
                  type="button"
                  onClick={() => setShowModal(false)}
                  className="px-4 py-2 border border-slate-200 text-slate-700 rounded-lg hover:bg-slate-50"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  className="px-4 py-2 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700"
                  data-testid="save-teacher-btn"
                >
                  {selectedTeacher ? 'Update Teacher' : 'Create Teacher'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default Teachers;
